import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { Loader2, Trash2 } from "lucide-react";
import { CSSProperties, ReactNode } from "react";
import { cn } from "../../lib/utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle
} from "../ui/common/alert-dialog";
import { buttonVariants } from "../ui/common/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../ui/common/card";

export interface ConfirmModalProps {
  viewModal?: boolean;
  closeModal?: () => void;
  cancel?: () => void;
  approve?: () => void;
  size?: "mini" | "tiny" | "small" | "large" | "fullscreen" | string;
  title?: ReactNode;
  subTitle?: ReactNode;
  otherConditionCheck?: boolean | Boolean;
  otherConditionBottomButton?: ReactNode;
  style?: CSSProperties;
  className?: string;
  mountNode?: HTMLElement | null;
  cancelText?: string;
  confirmText?: string;
  variant?: "destructive" | "default";
  testId?: string;
  isLoading?: boolean;
  icon?: ReactNode;
}

const sizeToMaxWidth: Record<string, string> = {
  mini: "sm:tw-max-w-md",
  tiny: "sm:tw-max-w-md",
  small: "sm:tw-max-w-lg",
  large: "sm:tw-max-w-xl",
  fullscreen: "sm:tw-max-w-4xl"
};

const ConfirmModal = ({
  viewModal = false,
  closeModal = () => {},
  cancel = () => {},
  approve = () => {},
  size = "mini",
  title = "",
  subTitle = "",
  otherConditionCheck = false,
  otherConditionBottomButton = null,
  style = {},
  className = "",
  mountNode = undefined,
  cancelText = "Cancel",
  confirmText = "Delete",
  variant = "destructive",
  testId,
  isLoading = false,
  icon
}: ConfirmModalProps) => {
  const handleOpenChange = (open: boolean) => {
    if (!open && !isLoading) {
      closeModal();
    }
  };

  const showCustomFooter = Boolean(otherConditionCheck);
  const isDestructive = variant === "destructive";

  return (
    <AlertDialog open={viewModal} onOpenChange={handleOpenChange}>
      <AlertDialogPortal container={mountNode ?? undefined}>
        <AlertDialogOverlay className="!tw-z-[10000] tw-bg-black/50 tw-backdrop-blur-sm" />
        <AlertDialogPrimitive.Content
          data-testid={testId}
          className={cn(
            "tw-fixed tw-left-[50%] tw-top-[50%] !tw-z-[10001] tw-w-full tw-max-w-lg tw-translate-x-[-50%] tw-translate-y-[-50%] tw-p-0 tw-gap-0 tw-border-0 tw-bg-transparent tw-shadow-none tw-duration-200",
            "data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0 data-[state=closed]:tw-zoom-out-95 data-[state=open]:tw-zoom-in-95",
            sizeToMaxWidth[size] ?? "sm:tw-max-w-md",
            className
          )}
          style={style}
        >
          <Card className="tw-rounded-xl tw-border tw-border-border tw-bg-background tw-shadow-lg tw-gap-0 tw-py-0">
            <CardHeader className="tw-flex tw-flex-row tw-items-center tw-gap-3 tw-space-y-0 tw-px-6 tw-pt-6 tw-pb-0">
              {isDestructive ? (
                <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-destructive/10">
                  {icon ?? <Trash2 className="tw-h-5 tw-w-5 tw-text-destructive" />}
                </div>
              ) : icon ? (
                <div className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-primary/10 tw-text-primary">
                  {icon}
                </div>
              ) : null}
              <CardTitle className="tw-p-0">
                <AlertDialogTitle className="tw-text-xl tw-font-semibold tw-leading-tight tw-text-foreground">
                  {title}
                </AlertDialogTitle>
              </CardTitle>
            </CardHeader>

            <CardContent className="tw-px-6 tw-pt-4 tw-pb-2">
              <AlertDialogDescription className="tw-text-sm tw-leading-relaxed tw-text-muted-foreground">
                {subTitle}
              </AlertDialogDescription>
            </CardContent>

            <CardFooter className="tw-flex tw-justify-end tw-gap-2 tw-px-6 tw-pb-6 tw-pt-4">
              {showCustomFooter ? (
                otherConditionBottomButton
              ) : (
                <>
                  <AlertDialogCancel type="button" onClick={cancel} disabled={isLoading}>
                    {cancelText}
                  </AlertDialogCancel>
                  <AlertDialogAction
                    type="button"
                    onClick={approve}
                    disabled={isLoading}
                    className={buttonVariants({ variant: isDestructive ? "destructive" : "default" })}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" />
                        {confirmText}
                      </>
                    ) : (
                      confirmText
                    )}
                  </AlertDialogAction>
                </>
              )}
            </CardFooter>
          </Card>
        </AlertDialogPrimitive.Content>
      </AlertDialogPortal>
    </AlertDialog>
  );
};

export default ConfirmModal;
